import { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate, Link } from "react-router-dom";
import { api, API, formatMoney } from "@/lib/api";
import { logger } from "@/lib/logger";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, Download, Upload, FileText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";

const TIPOS = [
  { key: "cpf", label: "CPF" }, { key: "rg", label: "RG" },
  { key: "cnh", label: "CNH (opcional)" }, { key: "comprovante_residencia", label: "Comprovante de Residência" },
  { key: "cadunico", label: "Folha Resumo CadÚnico" }, { key: "certidao_casamento", label: "Certidão de Casamento" },
  { key: "certidao_nascimento", label: "Certidão de Nascimento" }, { key: "laudo_pcd", label: "Laudo PCD" },
  { key: "outros", label: "Outros" },
];

export default function SucessoPage() {
  const { protocolo } = useParams();
  const { state } = useLocation();
  const nav = useNavigate();
  const [insc, setInsc] = useState(state?.inscricao || null);
  const [loading, setLoading] = useState(!state?.inscricao);
  const [uploading, setUploading] = useState({});

  useEffect(() => {
    if (state?.inscricao) return undefined;
    let active = true;
    api.get(`/inscricoes/protocolo/${protocolo}`)
      .then((r) => { if (active) setInsc(r.data); })
      .catch((e) => { logger.error("Falha ao consultar protocolo", e); toast.error("Inscrição não encontrada"); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [protocolo, state]);

  const upload = async (tipo, file) => {
    if (!file) return;
    if (!insc?.id) { toast.error("Faça login para anexar documentos depois."); return; }
    setUploading((p) => ({ ...p, [tipo]: true }));
    const fd = new FormData(); fd.append("tipo", tipo); fd.append("file", file);
    try {
      const r = await api.post(`/inscricoes/${insc.id}/documentos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Documento enviado!");
      setInsc((p) => ({ ...p, documentos: [...(p.documentos || []), r.data] }));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha no upload");
    } finally { setUploading((p) => ({ ...p, [tipo]: false })); }
  };

  if (loading) return <div className="p-12 text-center">Carregando...</div>;
  if (!insc) return <div className="p-12 text-center">Protocolo não encontrado.</div>;

  const baixar = () => {
    window.open(`${API}/inscricoes/protocolo/${insc.protocolo}/comprovante`, "_blank");
  };

  return (
    <div className="min-h-screen bg-[#F4F6F9]">
      <Header />
      <div className="max-w-4xl mx-auto px-4 py-8" data-testid="sucesso-page">
        <Card className="border-green-300 bg-green-50/50">
          <CardContent className="p-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-600 text-white mb-4">
              <CheckCircle2 className="w-9 h-9" />
            </div>
            <h1 className="text-3xl font-black text-[#083CA6] mb-2">Inscrição Realizada!</h1>
            <p className="text-slate-700 mb-6">Sua pré-inscrição foi recebida com sucesso. Guarde o número do protocolo para futuras consultas.</p>
            <div className="bg-white border-2 border-dashed border-[#083CA6] rounded-lg p-6 inline-block">
              <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">Protocolo</div>
              <div className="text-2xl md:text-3xl font-black text-[#083CA6] tracking-wider" data-testid="protocolo-numero">{insc.protocolo}</div>
              <div className="text-xs text-slate-500 mt-2">Status: {insc.status} {insc.apta_faixa1 !== undefined && (insc.apta_faixa1 ? "· Apta Faixa 1" : "· Fora da Faixa 1")}</div>
            </div>
            <div className="mt-6 flex justify-center gap-3 flex-wrap">
              <Button onClick={baixar} className="bg-[#083CA6] hover:bg-[#062f85]" data-testid="btn-baixar-comprovante">
                <Download className="w-4 h-4 mr-2" />Baixar Comprovante PDF
              </Button>
              <Link to="/"><Button variant="outline">Voltar ao Início</Button></Link>
            </div>
          </CardContent>
        </Card>

        {insc.id && (
          <Card className="mt-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Upload className="w-5 h-5" />Anexar Documentos</CardTitle>
              <p className="text-sm text-slate-600">Faça upload dos documentos (PDF, JPG, PNG até 20MB cada).</p>
            </CardHeader>
            <CardContent className="space-y-3">
              {TIPOS.map((t) => {
                const existing = (insc.documentos || []).find((d) => d.tipo === t.key);
                return (
                  <div key={t.key} className="flex items-center gap-3 border rounded p-3" data-testid={`upload-row-${t.key}`}>
                    <FileText className="w-5 h-5 text-slate-400" />
                    <div className="flex-1">
                      <Label className="font-semibold">{t.label}</Label>
                      {existing && <div className="text-xs text-green-700">✓ {existing.filename}</div>}
                    </div>
                    <Input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => upload(t.key, e.target.files[0])} className="max-w-[260px]" disabled={uploading[t.key]} data-testid={`upload-input-${t.key}`} />
                    {uploading[t.key] && <Loader2 className="w-4 h-4 animate-spin" />}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
